import React, { useEffect, useRef, useState } from 'react';
import WaveSurfer from 'wavesurfer.js';

import AudioPanelLatestStepMessage from './AudioPanelLatestStepMessage';
import AudioPlayer from '@experiment-management-shared/components/AudioPlayer';
import styles from './AudioThumbnail.module.scss';
import { AssetStep, AudioAssetData } from '@experiment-management-shared/types';
import { AudioDetailsModal } from './AudioDetailsModal';
import { DSFullScreenIcon } from '@ds-icons';
import { IconButton } from '@ds';
import debounce from 'lodash/debounce';

type AudioThumbnailProps = {
  name?: string;
  latestStep?: AssetStep;
  stepAPIValue: number | null;
  onClick?: () => void;
  src?: string;
  assets?: AudioAssetData[];
  asset?: AudioAssetData;
  showOnlyCurrentStepAudios?: boolean;
  setCurrentPlayingAudio: (id: WaveSurfer) => void;
};

export function AudioThumbnail({
  name = '',
  latestStep,
  stepAPIValue,
  onClick,
  src,
  assets,
  asset,
  showOnlyCurrentStepAudios,
  setCurrentPlayingAudio
}: AudioThumbnailProps) {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [containerWidth, setContainerWidth] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const isNotCurrentStep =
    !!asset && !!stepAPIValue && stepAPIValue !== asset?.step;

  useEffect(() => {
    if (!containerRef.current) return;

    const updateWidth = debounce((entries: ResizeObserverEntry[]) => {
      for (const entry of entries) {
        const newWidth = entry.contentRect.width;
        setContainerWidth(newWidth);
      }
    }, 100);

    const observer = new ResizeObserver(updateWidth);
    observer.observe(containerRef.current);

    return () => {
      observer.disconnect();
      updateWidth.cancel();
    };
  }, []);

  const renderAudio = () => {
    if (!src) {
      return <div className={styles.noAsset}>Audio not found</div>;
    }

    if (isNotCurrentStep && showOnlyCurrentStepAudios) {
      return null;
    }

    return (
      <AudioPlayer
        key={containerWidth}
        src={src}
        height={50}
        setCurrentPlayingAudio={setCurrentPlayingAudio}
      />
    );
  };

  return (
    <div
      className={styles.thumbnail}
      onClick={onClick}
      ref={containerRef}
      data-test={`audio-thumbnail-${name}`}
    >
      {isNotCurrentStep && !showOnlyCurrentStepAudios && (
        <AudioPanelLatestStepMessage
          currentStep={stepAPIValue}
          latestStep={latestStep}
        />
      )}
      {asset && (
        <div className={styles.fullScreenIcon}>
          <IconButton
            type="secondary"
            size="small"
            Icon={<DSFullScreenIcon />}
            onClick={() => setIsFullscreen(true)}
          />
        </div>
      )}
      {isFullscreen && assets && asset && (
        <AudioDetailsModal
          assets={assets}
          onClose={() => setIsFullscreen(false)}
          selectedAsset={asset}
        />
      )}
      {renderAudio()}
    </div>
  );
}
